import { Link } from "react-router-dom";
import { Check, Sparkles, ArrowRight } from "lucide-react";

/**
 * Pricing preview — four self-serve tiers.
 *
 * The Pro card is marked "Most popular" and gets an amber outline +
 * accent header — the standard SaaS "pull one plan" pattern that lifts
 * conversion by giving the eye a default.
 *
 * All numbers come from the live catalog (Basic €29 · Pro €40 ·
 * Pro Max €69 · Pro Annual €399). Full plan comparison lives on the
 * dedicated /billing page — this section is a taste + CTA, not a spec.
 */
const PLANS = [
  {
    slug: "basic",
    name: "Basic",
    price: "€29",
    cadence: "/mo",
    tagline: "For solo operators just leaving spreadsheets.",
    bullets: [
      "Up to 100 bookings / month",
      "1 staff seat",
      "PDF invoicing (VAT · cash)",
      "Email support",
    ],
    cta: "Start on Basic",
    highlight: false,
  },
  {
    slug: "pro",
    name: "Pro",
    price: "€40",
    cadence: "/mo",
    tagline: "For growing teams. The one most people pick.",
    bullets: [
      "Unlimited bookings",
      "5 staff seats",
      "RCT & multi-template invoicing",
      "Staff cash ledger + settlements",
      "Priority support",
    ],
    cta: "Start on Pro",
    highlight: true,
    badge: "Most popular",
  },
  {
    slug: "pro_unlimited",
    name: "Pro Max",
    price: "€69",
    cadence: "/mo",
    tagline: "Unlimited staff, unlimited scale.",
    bullets: [
      "Everything in Pro",
      "Unlimited staff seats",
      "Advanced routing & travel",
      "Custom invoice templates",
    ],
    cta: "Go Pro Max",
    highlight: false,
  },
  {
    slug: "pro_annual",
    name: "Pro Annual",
    price: "€399",
    cadence: "/yr",
    tagline: "Pro for the year. Save ~€80 vs monthly.",
    bullets: [
      "Everything in Pro",
      "12 months up front",
      "Priority onboarding",
      "Locked-in price",
    ],
    cta: "Pay annually",
    highlight: false,
    save: "Save €81",
  },
];

export default function PricingPreview() {
  return (
    <section
      id="pricing"
      className="bg-background scroll-mt-20"
      data-testid="landing-pricing"
    >
      <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-20 md:py-28">
        <div className="max-w-2xl">
          <div className="mono text-[11px] uppercase tracking-[0.3em] text-accent">
            // Pricing
          </div>
          <h2 className="mt-3 text-3xl md:text-5xl font-black tracking-tight leading-[1.05]">
            Simple plans. No per-invoice fees.
          </h2>
          <p className="mt-4 text-base md:text-lg text-muted-foreground leading-relaxed">
            Every plan includes a 7-day free trial. Cancel anytime. Prices in EUR, VAT excluded.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5 mt-12">
          {PLANS.map((p) => (
            <article
              key={p.slug}
              data-testid={`pricing-card-${p.slug}`}
              className={`relative rounded-xl bg-card border p-6 flex flex-col transition-all hover:-translate-y-1 hover:shadow-xl ${
                p.highlight
                  ? "border-accent shadow-[0_0_0_1px_hsl(var(--accent))]"
                  : "border-border hover:border-accent/60"
              }`}
            >
              {p.highlight && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 mono text-[10px] uppercase tracking-[0.25em] bg-accent text-accent-foreground px-2.5 py-1 rounded-full font-bold">
                  <Sparkles className="h-3 w-3" /> {p.badge}
                </div>
              )}
              {p.save && (
                <div className="absolute top-4 right-4 mono text-[9px] uppercase tracking-widest bg-emerald-50 text-emerald-700 border border-emerald-200 px-1.5 py-0.5 rounded">
                  {p.save}
                </div>
              )}

              <h3 className="font-black text-xl">{p.name}</h3>
              <div className="mt-1 text-xs text-muted-foreground leading-relaxed min-h-[36px]">
                {p.tagline}
              </div>

              <div className="mt-5 flex items-baseline gap-1">
                <span className="text-4xl md:text-5xl font-black tracking-tight">{p.price}</span>
                <span className="text-muted-foreground text-sm">{p.cadence}</span>
              </div>

              <ul className="mt-6 space-y-2.5 flex-1">
                {p.bullets.map((b) => (
                  <li key={b} className="flex items-start gap-2 text-sm">
                    <Check className={`h-4 w-4 shrink-0 mt-0.5 ${p.highlight ? "text-accent" : "text-muted-foreground"}`} />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>

              <Link
                to="/signup"
                className={`mt-6 inline-flex items-center justify-center gap-2 py-3 rounded-md text-sm font-bold transition-all ${
                  p.highlight
                    ? "bg-accent text-accent-foreground hover:bg-accent/90"
                    : "border border-border hover:border-accent hover:bg-secondary"
                }`}
                data-testid={`pricing-cta-${p.slug}`}
              >
                {p.cta} <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </article>
          ))}
        </div>

        <div className="mt-8 text-center text-xs text-muted-foreground">
          Need custom pricing for 10+ crews?{" "}
          <a href="mailto:info@jobsbook.app" className="underline hover:text-accent">
            Talk to us
          </a>
          .
        </div>
      </div>
    </section>
  );
}
